import { createContext, useContext, useState, useEffect } from "react";
import API from "../config/api";
import { useLanguage } from "../i18n/LanguageProvider";

const HomeContext = createContext();

function getInitialHomeData(language) {
    if (typeof window === "undefined") {
        return {};
    }

    const initialData = window.__LIDERE_INITIAL_DATA__;

    if (!initialData || initialData.language !== language || !initialData.home) {
        return {};
    }

    return initialData.home;
}

function getInitialDirectory(language) {
    if (typeof window === "undefined") {
        return null;
    }

    const initialData = window.__LIDERE_INITIAL_DATA__;

    if (!initialData || initialData.language !== language || !initialData.directory) {
        return null;
    }

    return initialData.directory;
}

export function HomeProvider({ children }) {
    const { language } = useLanguage();
    const initialHomeData = getInitialHomeData(language);
    const [topWomen, setTopWomen] = useState(() => initialHomeData.topWomen || null);
    const [defaultWomen, setDefaultWomen] = useState(() => initialHomeData.defaultWomen || null);
    const [monthlyWoman, setMonthlyWoman] = useState(() => initialHomeData.monthlyWoman || null);
    const [news, setNews] = useState(() => initialHomeData.news || null);
    const [directory, setDirectory] = useState(() => getInitialDirectory(language));
    const [initialGameData, setInitialGameData] = useState(null);

    useEffect(() => {
        Promise.all([
            fetch(`${API.getNews}?limit=3&lang=${language}`, {
                credentials: "include",
            })
                .then((res) => res.json())
                .catch(() => ({ data: [] })),
            fetch(`${API.getWoman}?monthly=true&lang=${language}`, {
                credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API.getWoman}?isTop=true&lang=${language}`).then((res) => res.json()),
            fetch(`${API.getWoman}?isDefault=true&lang=${language}`, {
                credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API.minigame}?lang=${language}`, {
                credentials: "include",
            }).then((res) => res.json()),
            fetch(`${API.getWoman}?taxonomy=true&lang=${language}`, {
                credentials: "include",
            })
                .then((res) => res.json())
                .catch(() => null),
        ])
            .then(([newsData, monthlyData, topData, defaultData, gameData, directoryData]) => {
                setDirectory((currentDirectory) => directoryData?.data || currentDirectory || null);
                setNews(newsData?.data || []);
                setMonthlyWoman((currentWoman) => monthlyData?.data || currentWoman || null);
                setTopWomen(topData);
                setDefaultWomen(defaultData);
                setInitialGameData(gameData);
            })
            .catch((err) => console.error("Error loading home data:", err));
    }, [language]);

    return (
        <HomeContext.Provider
            value={{
                topWomen,
                defaultWomen,
                monthlyWoman,
                news,
                directory,
                initialGameData,
            }}>
            {children}
        </HomeContext.Provider>
    );
}

export function useHomeData() {
    return useContext(HomeContext);
}
